/**
 * AutoplaySystem — A real-time bot that plays the game using strategic logic.
 *
 * Uses StrategicSimulator.makeDispatchDecision() for each dispatch —
 * identical logic to the audit simulator, including deep rollout search.
 *
 * Decision logic is offloaded to a Web Worker to avoid blocking the main
 * thread during deep rollout search (which can take 500ms-5s).
 *
 * Async pattern:
 * 1. requestDecision() — posts MidGameState to worker
 * 2. Worker runs makeDispatchDecision() off-thread
 * 3. consumeDecision() — returns the result when ready
 *
 * The synchronous decide() method is kept as a fallback.
 */

import type { BoardSystem } from './BoardSystem';
import type { ConveyorSystem } from './ConveyorSystem';
import type { QueueSystem } from './QueueSystem';
import type { StackSystem } from './StackSystem';
import { attackerSize, type AttackerData } from '../entities/Attacker';
import { CONVEYOR_MAX_ATTACKERS, RUNTIME_LOOKAHEAD_DEPTH, RUNTIME_ROLLOUT_DEPTH } from '../config/constants';
import {
  createBotTrace,
  formatTraceText,
  type BotMegaBlock,
  type BotTrace,
  type BotWall,
  type BotDoor,
  type ScoreBreakdown,
} from './BotStrategy';
import { StrategicSimulator, type MidGameState, type DispatchDecision, type SimAttackerData } from './StrategicSimulator';
import { cellKey } from '../types/board';
import type { SerializedMidGameState } from './autoplay.worker';

/** Resolves an attacker ID to its data (orbs, ammo, etc). */
export type AttackerResolver = (attackerId: number) => AttackerData | undefined;

export interface ScoringInfo {
  breakdown: ScoreBreakdown;
  candidateCount: number;
}

export type AutoplayAction =
  | { type: 'dispatch_queue'; lane: number; scoring?: ScoringInfo }
  | { type: 'dispatch_stack'; slotIndex: number; scoring?: ScoringInfo }
  | { type: 'wait' };

export class AutoplaySystem {
  private board: BoardSystem;
  private conveyor: ConveyorSystem;
  private queue: QueueSystem;
  private stack: StackSystem;
  private resolveAttacker: AttackerResolver;

  private trace: BotTrace | null = null;
  private traceTick: number = 0;

  /** How often the bot makes decisions (ms). */
  readonly tickInterval: number;

  // ── Web Worker fields ──────────────────────────────────────────────
  private worker: Worker | null = null;
  private pendingDecision: AutoplayAction | null = null;
  private waitingForWorker: boolean = false;

  // ── Full-game solve replay fields ───────────────────────────────────
  private replaySequence: DispatchDecision[] | null = null;
  private replayIndex: number = 0;
  private solvingInProgress: boolean = false;

  constructor(
    board: BoardSystem,
    conveyor: ConveyorSystem,
    queue: QueueSystem,
    stack: StackSystem,
    resolveAttacker: AttackerResolver,
    tickInterval: number = 750,
  ) {
    this.board = board;
    this.conveyor = conveyor;
    this.queue = queue;
    this.stack = stack;
    this.resolveAttacker = resolveAttacker;
    this.tickInterval = tickInterval;

    // Spawn the Web Worker
    this.initWorker();
  }

  private initWorker(): void {
    try {
      this.worker = new Worker(
        new URL('./autoplay.worker.ts', import.meta.url),
        { type: 'module' },
      );
      this.worker.onmessage = (e: MessageEvent<DispatchDecision | { type: 'solution'; solution: DispatchDecision[]; won: boolean }>) => {
        const data = e.data;
        // Full-game solution response
        if (data && typeof data === 'object' && 'type' in data && data.type === 'solution') {
          const msg = data as { type: 'solution'; solution: DispatchDecision[]; won: boolean };
          this.replaySequence = msg.solution;
          this.replayIndex = 0;
          this.solvingInProgress = false;
          return;
        }
        // Single dispatch decision response
        this.pendingDecision = this.mapDecision(data as DispatchDecision);
        this.waitingForWorker = false;
      };
      this.worker.onerror = (err) => {
        console.error('[AutoplaySystem] Worker error:', err);
        this.waitingForWorker = false;
        this.solvingInProgress = false;
      };
    } catch (err) {
      console.warn('[AutoplaySystem] Failed to create Worker, falling back to synchronous mode:', err);
      this.worker = null;
    }
  }

  // ── Trace controls ──────────────────────────────────────────────────

  enableTrace(): void {
    this.trace = createBotTrace();
    this.traceTick = 0;
  }

  disableTrace(): BotTrace | null {
    const t = this.trace;
    this.trace = null;
    this.traceTick = 0;
    return t;
  }

  get traceActive(): boolean {
    return this.trace !== null;
  }

  downloadTrace(): void {
    const t = this.disableTrace();
    if (!t || t.entries.length === 0) return;
    this.downloadTraceData(t);
  }

  /** Download a snapshot of the current trace without stopping tracing. */
  snapshotTrace(): void {
    if (!this.trace || this.trace.entries.length === 0) return;
    this.downloadTraceData(this.trace);
  }

  private downloadTraceData(t: BotTrace): void {
    const text = formatTraceText(t.entries);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bot-trace-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Worker-based async decision ─────────────────────────────────────

  /**
   * Post the current game state to the worker for async decision making.
   * Call this when the conveyor has space and we're not already waiting.
   */
  requestDecision(): void {
    if (this.waitingForWorker) return;
    if (!this.worker) {
      // Fallback: synchronous decision
      this.pendingDecision = this.decide();
      return;
    }

    const serialized = this.buildSerializedState();
    // Large grids: truncated rollouts (200 ticks on 1000+ tick games) produce
    // near-identical scores for all candidates, making the bot pick at random.
    // Use greedy scorer (rolloutDepth 0) for locally sound decisions instead.
    serialized.rolloutDepth = 0;
    this.waitingForWorker = true;
    this.pendingDecision = null;
    this.worker.postMessage(serialized);
  }

  /**
   * Returns the next decision to execute. If a replay sequence exists,
   * returns the next step from it. Otherwise returns the pending single
   * decision from the worker, or null if nothing is ready.
   */
  consumeDecision(): AutoplayAction | null {
    // Replay sequence takes priority
    if (this.replaySequence && this.replayIndex < this.replaySequence.length) {
      const decision = this.replaySequence[this.replayIndex++];
      return this.mapDecision(decision);
    }

    // Fall back to single-decision mode
    const d = this.pendingDecision;
    this.pendingDecision = null;
    return d;
  }

  /** Whether we're currently waiting for the worker to return a decision. */
  isWaiting(): boolean {
    return this.waitingForWorker;
  }

  // ── Full-game solve ──────────────────────────────────────────────────

  /** Grid cell threshold above which full-game solve is skipped in favor of incremental mode. */
  private static readonly FULL_SOLVE_MAX_CELLS = 2500;

  /**
   * Request a full-game solution from the worker. Posts the current game
   * state with solve_full_game type. When the worker responds, the solution
   * is stored in replaySequence for consumption via consumeDecision().
   *
   * For large grids (>2500 cells), falls back to incremental single-decision
   * mode since full-game solve is computationally infeasible.
   */
  requestFullSolution(): void {
    if (this.solvingInProgress) return;
    if (!this.worker) {
      console.warn('[AutoplaySystem] No worker available for full solve');
      return;
    }

    // Large grids (e.g. 128x128 cute levels) are too expensive for full-game
    // solve — fall back to incremental single-decision mode.
    const cells = this.board.rows * this.board.cols;
    if (cells > AutoplaySystem.FULL_SOLVE_MAX_CELLS) {
      this.requestDecision();
      return;
    }

    const serialized = this.buildSerializedState();
    this.solvingInProgress = true;
    this.replaySequence = null;
    this.replayIndex = 0;
    this.worker.postMessage({ type: 'solve_full_game', state: serialized });
  }

  /** Whether a full-game solve is currently in progress in the worker. */
  isSolving(): boolean {
    return this.solvingInProgress;
  }

  /** Whether a replay sequence is loaded and has steps remaining. */
  hasReplay(): boolean {
    return this.replaySequence !== null && this.replayIndex < this.replaySequence.length;
  }

  /**
   * Discard the pre-computed replay sequence. Called when the player makes
   * a manual move that invalidates the pre-computed solution.
   */
  resetReplay(): void {
    this.replaySequence = null;
    this.replayIndex = 0;
  }

  /** Terminate the worker. Call on scene shutdown / cleanup. */
  destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingDecision = null;
    this.waitingForWorker = false;
    this.replaySequence = null;
    this.replayIndex = 0;
    this.solvingInProgress = false;
  }

  // ── State Extraction Helpers ────────────────────────────────────────

  private toSimAttacker(att: AttackerData): SimAttackerData {
    return {
      orbs: att.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
      size: attackerSize(att),
      frozen: att.frozen ?? 0,
    };
  }

  private extractQueueLanes(): MidGameState['queueLanes'] {
    const lanes: MidGameState['queueLanes'] = [];
    for (let lane = 0; lane < this.queue.numLanes(); lane++) {
      const laneData = this.queue.getQueueLane(lane);
      lanes.push(laneData.map(att => this.toSimAttacker(att)));
    }
    return lanes;
  }

  private extractConveyor(): SimAttackerData[] {
    const result: SimAttackerData[] = [];
    for (const entry of this.conveyor.getAttackerPositions()) {
      const att = this.resolveAttacker(entry.attackerId);
      if (!att) continue;
      result.push(this.toSimAttacker(att));
    }
    return result;
  }

  private extractStack(): SimAttackerData[] {
    const result: SimAttackerData[] = [];
    const slots = this.stack.getSlots();
    const seen = new Set<number>();
    for (const att of slots) {
      if (!att || seen.has(att.id)) continue;
      seen.add(att.id);
      result.push(this.toSimAttacker(att));
    }
    return result;
  }

  /** Map a raw DispatchDecision from the simulator to an AutoplayAction. */
  private mapDecision(decision: DispatchDecision): AutoplayAction {
    switch (decision.action) {
      case 'dispatch_stack':
        return { type: 'dispatch_stack', slotIndex: decision.index! };
      case 'dispatch_queue':
        return { type: 'dispatch_queue', lane: decision.index! };
      case 'wait':
        return { type: 'wait' };
    }
  }

  /**
   * Evaluate the current game state and decide what to do (SYNCHRONOUS fallback).
   * Returns an action: dispatch from queue, dispatch from stack, or wait.
   *
   * Uses StrategicSimulator.makeDispatchDecision() which applies the full
   * simulator logic including rollout search when RUNTIME_ROLLOUT_DEPTH > 0.
   */
  decide(): AutoplayAction {
    const boardSnapshot = this.buildBoardSnapshot();

    // Build MidGameState from live Phaser systems
    const state: MidGameState = {
      grid: boardSnapshot.grid,
      queueLanes: this.extractQueueLanes(),
      conveyor: this.extractConveyor(),
      stack: this.extractStack(),
      maxConveyor: CONVEYOR_MAX_ATTACKERS,
      maxStack: this.stack.getCapacity(),
      numQueues: this.queue.numLanes(),
      snakeCells: boardSnapshot.snakeCells,
      doorCells: boardSnapshot.doorCells,
      barricadeCells: boardSnapshot.barricadeCells,
      lookaheadDepth: RUNTIME_LOOKAHEAD_DEPTH,
      rolloutDepth: RUNTIME_ROLLOUT_DEPTH,
    };

    // Add mega blocks if present
    if (boardSnapshot.megaBlocks.size > 0) {
      state.megaBlockDefs = Array.from(boardSnapshot.megaBlocks.values()).map(mb => ({
        colorIndex: mb.colorIndex,
        hp: mb.hp,
        row: mb.row,
        col: mb.col,
        width: mb.width,
        height: mb.height,
      }));
    }

    // Add blockade walls if present (live HP captured from board)
    if (boardSnapshot.walls.size > 0) {
      state.blockadeWalls = Array.from(boardSnapshot.walls.values()).map(w => ({ ...w }));
    }

    // Add locked doors if present (live HP captured from board)
    if (boardSnapshot.doors.size > 0) {
      state.lockedDoors = Array.from(boardSnapshot.doors.values()).map(d => ({ ...d }));
    }

    const decision = StrategicSimulator.makeDispatchDecision(state);
    return this.mapDecision(decision);
  }

  /**
   * Build a serialized MidGameState suitable for postMessage to the worker.
   * Converts Set<number> fields to number[] arrays.
   */
  private buildSerializedState(): SerializedMidGameState {
    const boardSnapshot = this.buildBoardSnapshot();

    const state: SerializedMidGameState = {
      grid: boardSnapshot.grid,
      queueLanes: this.extractQueueLanes(),
      conveyor: this.extractConveyor(),
      stack: this.extractStack(),
      maxConveyor: CONVEYOR_MAX_ATTACKERS,
      maxStack: this.stack.getCapacity(),
      numQueues: this.queue.numLanes(),
      snakeCells: boardSnapshot.snakeCells.size > 0 ? Array.from(boardSnapshot.snakeCells) : undefined,
      doorCells: boardSnapshot.doorCells.size > 0 ? Array.from(boardSnapshot.doorCells) : undefined,
      barricadeCells: boardSnapshot.barricadeCells.size > 0 ? Array.from(boardSnapshot.barricadeCells) : undefined,
      lookaheadDepth: RUNTIME_LOOKAHEAD_DEPTH,
      rolloutDepth: RUNTIME_ROLLOUT_DEPTH,
    };

    // Add mega blocks if present
    if (boardSnapshot.megaBlocks.size > 0) {
      state.megaBlockDefs = Array.from(boardSnapshot.megaBlocks.values()).map(mb => ({
        colorIndex: mb.colorIndex,
        hp: mb.hp,
        row: mb.row,
        col: mb.col,
        width: mb.width,
        height: mb.height,
      }));
    }

    // Add blockade walls if present (live HP captured from board)
    if (boardSnapshot.walls.size > 0) {
      state.blockadeWalls = Array.from(boardSnapshot.walls.values()).map(w => ({ ...w }));
    }

    // Add locked doors if present (live HP captured from board)
    if (boardSnapshot.doors.size > 0) {
      state.lockedDoors = Array.from(boardSnapshot.doors.values()).map(d => ({ ...d }));
    }

    return state;
  }

  // ── Board Snapshot from Live Game Objects ────────────────────────

  private buildBoardSnapshot() {
    const rows = this.board.rows;
    const cols = this.board.cols;

    // Build grid from live board
    const grid: number[][][] = [];
    for (let r = 0; r < rows; r++) {
      const row: number[][] = [];
      for (let c = 0; c < cols; c++) {
        row.push([...this.board.getStack(r, c)]);
      }
      grid.push(row);
    }

    // Build snake cells set
    const snakeCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isSnakeCell(r, c)) {
          snakeCells.add(cellKey(r, c));
        }
      }
    }

    // Build mega blocks
    const megaBlocks = new Map<number, BotMegaBlock>();
    const megaCells = new Set<number>();
    const activeMegaBlocks = this.board.getMegaBlocks();
    if (activeMegaBlocks) {
      for (const [id, mb] of activeMegaBlocks) {
        megaBlocks.set(id, {
          id,
          colorIndex: mb.colorIndex,
          hp: mb.hp,
          row: mb.row,
          col: mb.col,
          width: mb.width,
          height: mb.height,
        });
        for (let r = mb.row; r < mb.row + mb.height; r++) {
          for (let c = mb.col; c < mb.col + mb.width; c++) {
            megaCells.add(cellKey(r, c));
          }
        }
      }
    }

    // Build door cells
    const doorCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isDoorCell(r, c)) {
          doorCells.add(cellKey(r, c));
        }
      }
    }

    // Build barricade cells
    const barricadeCells = new Set<number>();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.board.isBarricadeCell(r, c)) {
          barricadeCells.add(cellKey(r, c));
        }
      }
    }

    // Live blockade walls — current HP, keyed by wall id (1-based).
    const walls = new Map<number, BotWall>();
    const activeWalls = this.board.getActiveBlockadeWalls();
    for (const [id, w] of activeWalls) {
      walls.set(id, {
        id,
        side: w.side,
        startCell: w.startCell,
        length: w.length,
        colorIndex: w.colorIndex,
        hp: w.hp,
      });
    }

    // Live locked doors — current HP + trigger color, keyed by door id (1-based).
    const doors = new Map<number, BotDoor>();
    const activeDoors = this.board.getActiveDoors();
    for (const [id, d] of activeDoors) {
      doors.set(id, {
        id,
        triggerColor: d.colorIndex,
        hp: d.hp,
      });
    }

    return {
      grid,
      rows,
      cols,
      snakeCells,
      megaBlocks,
      megaCells,
      doorCells,
      barricadeCells,
      walls,
      doors,
    };
  }
}
